import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { Timestamp } from 'firebase/firestore';
import { CouponDoc } from '~/services/db/coupons/CouponsAPI';
import { OfferDoc } from '~/services/db/offers/OffersAPI';
import { CODE_CLIENT_ID, CODE_LANG } from '~/config/endpoints.config';
import { verifyCouponToken, generateCouponToken } from '~/utils/couponToken';
import { CouponCloudFunctionAPI } from '~/services/coupons/CouponCloudFunctionAPI';
import { CouponRewardScreen } from '~/components/coupon/components/CouponRewardScreen';
import { LoadingPage } from '~/pages/LoadingPage';
import { WarningPage } from '~/pages/WarningPage/WarningPage';
import logo from '~/assets/img/logo-unboared.png';
import styles from './RewardPage.module.css';

export function RewardPage() {
    const { t, i18n } = useTranslation();
    const { couponId } = useParams<{ couponId: string }>();
    const [searchParams] = useSearchParams();
    const clientID = searchParams.get(CODE_CLIENT_ID);
    const lang = searchParams.get(CODE_LANG) || 'en';
    const accessToken = searchParams.get('token');
    const [userData] = useLocalStorage<any>('userData', {});

    useEffect(() => {
        if (lang) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    const [coupon, setCoupon] = useState<CouponDoc | null>(null);
    const [offer, setOffer] = useState<OfferDoc | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(null);

    // Fetch coupon data via Cloud Function using token authentication
    useEffect(() => {
        async function fetchCouponData() {
            if (!couponId || !clientID) {
                setError('Missing coupon ID or client ID : ' + couponId + ' or ' + clientID);
                setLoading(false);
                return;
            }

            try {
                let authToken: string | null = null;
                let playerId: string | null = null;

                // Use token from URL if available
                if (accessToken) {
                    playerId = await verifyCouponToken(accessToken, couponId, clientID);
                    if (!playerId) {
                        setError('Invalid access token');
                        setLoading(false);
                        return;
                    }
                    authToken = accessToken;
                } else {
                    // Fallback: try to get playerId from localStorage (if accessing from game)
                    playerId = userData?.uid || null;

                    // If no token and no uid, we can't authenticate
                    if (!playerId) {
                        setError('Missing access token. Please use the link from your email or access from the game.');
                        setLoading(false);
                        return;
                    }

                    // Generate a token for the cloud function
                    authToken = await generateCouponToken(playerId, couponId, clientID);
                }

                // Store token for use in child components
                setToken(authToken);

                // Call cloud function to fetch coupon data
                const result = await CouponCloudFunctionAPI.getCouponData({
                    token: authToken,
                    couponId,
                    clientID,
                });

                if (!result.success || !result.coupon) {
                    setError(result.message || 'Failed to fetch coupon data');
                    setLoading(false);
                    return;
                }

                const couponData: CouponDoc = {
                    ...result.coupon,
                    issuedAt: Timestamp.fromDate(new Date(result.coupon.issuedAt)),
                    validUntil: Timestamp.fromDate(new Date(result.coupon.validUntil)),
                };

                setCoupon(couponData);

                if (result.offer) {
                    setOffer(result.offer as OfferDoc);
                }
            } catch (err: any) {
                console.error('Failed to fetch coupon:', err);
                setError(err.message || 'Failed to load coupon');
            } finally {
                setLoading(false);
            }
        }

        fetchCouponData();
    }, [couponId, clientID, accessToken, userData?.uid]);

    if (loading) {
        return <LoadingPage>{t('coupon.rewardPage.loading')}</LoadingPage>;
    }

    if (error || !coupon) {
        return (
            <WarningPage message={error || t('coupon.rewardPage.notFound')}>
                <p>{t('coupon.rewardPage.notFoundMessage')}</p>
            </WarningPage>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <img className={styles.logo} src={logo} alt="unboared logo" />
                {coupon?.clientName && (
                    <div className={styles.clientInfo}>
                        <div className={styles.gameStatus}>{t('coupon.rewardPage.gameStatus')}</div>
                        <div className={styles.clientName}>{coupon.clientName}</div>
                    </div>
                )}
            </div>
            <CouponRewardScreen coupon={coupon} offer={offer} clientID={clientID || ''} token={token} />
        </div>
    );
}
